import {
  classRegistry,
  Canvas as FabricCanvas,
  FabricObject,
  Point as FabricPoint,
  iMatrix,
  TPointerEvent,
} from 'fabric';
import { TEvent, TOptionalModifierKey } from 'fabric/src/EventTypeDefs';
import { useEffect, useState } from 'react';

import addSelectedMediaElementsOperation from 'editor/src/store/editor/operation/addSelectedMediaElementsOperation';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import getSelectedElementUuids from 'editor/src/store/editor/selector/getSelectedElementUuids';
import isAnyMediaElementInCropMode from 'editor/src/store/editor/selector/isAnyMediaElementInCropMode';
import { setZoomModeAction as setZoomModeOperation } from 'editor/src/store/editor/slice';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';

import CustomFabricActiveSelection from 'editor/src/fabric/CustomFabricActiveSelection';
import CustomFabricObject from 'editor/src/fabric/CustomFabricObject';

import { useIsMobile } from '../useDetectDeviceType';

import orderObjects from './orderObjects';
import useRenderCanvasOnPageVisible from './useRenderCanvasOnPageVisible';
import { getActiveSelection, getSelectionUuids } from './utils/groupSelectionUtils';

const SELECTION_BORDER_COLOR = 'rgba(71, 132, 255, 0.2)';
const SELECTION_COLOR = 'rgba(71, 132, 255, 0.2)';
const EMPTY_SELECTION_COLOR = 'rgba(0, 0, 0, 0)';

function useCreateFabricCanvas(canvasId: string) {
  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas>();
  const dispatch = useDispatch();
  const store = useStore();
  const isMobile = useIsMobile();

  const isAnyImageInCropMode = useSelector(isAnyMediaElementInCropMode);

  useEffect(() => {
    if (fabricCanvas) {
      fabricCanvas.uniScaleKey = (isAnyImageInCropMode ? 'shiftKey' : null) as TOptionalModifierKey;
      fabricCanvas.requestRenderAll();
    }
  }, [isAnyImageInCropMode]);

  useEffect(() => {
    classRegistry.setClass(CustomFabricActiveSelection, 'ActiveSelection');
    const canvas = new FabricCanvas(canvasId, {
      selection: true,
      renderOnAddRemove: false,
      preserveObjectStacking: true, // to avoid having the active elements always on top
      uniScaleKey: null,
      centeredKey: null,
      selectionBorderColor: isMobile ? EMPTY_SELECTION_COLOR : SELECTION_BORDER_COLOR,
      selectionColor: isMobile ? EMPTY_SELECTION_COLOR : SELECTION_COLOR,
    });

    (window as any).fabricCanvas = canvas;
    (window as any).getFabricObjectRect = (obj: FabricObject) => {
      if (!obj || !canvas) {
        return null;
      }

      // Get bounding rect (considers transformations like scaling/rotation)
      const boundingRect = obj.getBoundingRect();

      // Get canvas viewport transform (accounts for zoom & pan)
      const canvasTransform = canvas.viewportTransform || iMatrix; // Default to identity matrix if missing

      // Convert bounding box size to absolute scale
      const topLeft = new FabricPoint(boundingRect.left, boundingRect.top).transform(canvasTransform);
      const bottomRight = new FabricPoint(
        boundingRect.left + boundingRect.width,
        boundingRect.top + boundingRect.height,
      ).transform(canvasTransform);

      return {
        left: topLeft.x,
        top: topLeft.y,
        width: bottomRight.x - topLeft.x,
        height: bottomRight.y - topLeft.y,
      };
    };

    const onSelectionCreated = (event: Partial<TEvent<TPointerEvent>>) => {
      const elementUids = getSelectionUuids((event as any).selected);
      dispatch(addSelectedMediaElementsOperation(elementUids));
    };

    canvas.on('selection:cleared', () => {
      const selectedMediaElements = getSelectedElementUuids(store.getState());
      if (selectedMediaElements.length) {
        dispatch(removeAllSelectedMediaElementsOperation());
        dispatch(setZoomModeOperation(false)); // what is that for ?
      }
    });

    canvas.on('selection:created', onSelectionCreated);
    canvas.on('selection:updated', (event) => {
      let elementUids = getSelectionUuids((event as any).selected);
      const activeSelection = getActiveSelection(canvas);
      if (activeSelection) {
        elementUids = getSelectionUuids(activeSelection.getObjects() as CustomFabricObject[]);
      } else {
        const currentActiveElementUid = (canvas.getActiveObject() as any)?.uuid;
        if (event.deselected?.length && currentActiveElementUid) {
          elementUids = [currentActiveElementUid];
        }
      }

      if (elementUids.length) {
        dispatch(addSelectedMediaElementsOperation(elementUids));
      }
    });

    canvas.on('before:render', () => {
      orderObjects(canvas);
    });

    setFabricCanvas(canvas);
  }, [isMobile]);

  useRenderCanvasOnPageVisible(fabricCanvas);

  return fabricCanvas;
}

export default useCreateFabricCanvas;
